import React, { useState, useEffect, useRef, memo } from 'react';
import {
  X,
  Edit3,
  LucideArrowRightToLine,
  LucideArrowLeftToLine,
  Trash2,
  MoveRight,
} from 'lucide-react';
import axios from 'axios';
import ScenePreview from '../ScenePreview';
import { BASE_URL } from '../../utils/utils';
import { iconsPath, imagePath } from '../../utils/imagePath';
import { useNavigate } from 'react-router-dom';

import toast from 'react-hot-toast';
import useClickOutside from '../UseClickOutside';

import { useSelector } from 'react-redux';

function ChatInterface({
  selectedThreadId,
  setSelectedThreadId,
  messages,
  setMessages,
  experienceId,
  setExperienceId,
  query,

  toggleListening,
  isListening,
  setQuery,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatThreads, setChatThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [originalProjectName, setOriginalProjectName] = useState('');
  const [projectName, setProjectName] = useState('Untitled');
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);

  const { showChatInterface } = useSelector((state) => state.data);

  const [assets, setAssets] = useState({
    mesh: [],
    image: [],
    video: [],
    audio: [],
  });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [sketchfabStatus, setSketchfabStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const projectNameInputRef = useRef(null);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const historyRef = useRef(null);

  useClickOutside(historyRef, () => {
    setSidebarOpen(false);
    setShowDeleteConfirm(false);
  });

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom && messages.length > 0);
    }
  };

  function extractExperienceId(text) {
    try {
      const parsed = JSON.parse(text);
      return parsed.experienceId || null;
    } catch {
      const match = text.match(/"experienceId"\s*:\s*"([a-f0-9]+)"/);
      return match ? match[1] : null;
    }
  }

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [messages.length]);

  useEffect(() => {
    const latestMessage = messages[messages.length - 1];
    if (latestMessage && latestMessage.sender === 'ai') {
      const newExperienceId = extractExperienceId(
        latestMessage.rawText || latestMessage.text
      );
      if (newExperienceId && newExperienceId !== experienceId) {
        setExperienceId(newExperienceId);
        fetchAssetsForExperience(newExperienceId);
      }
    }
  }, [messages, experienceId]);

  useEffect(() => {
    const fetchThreads = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_IMAGE_BASE_URL}/user/threads`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setChatThreads(res.data.threads || []);
      } catch (err) {
        console.error('Error fetching threads:', err);
        toast.error('Failed to load chat threads');
      }
    };

    fetchThreads();
  }, [token]);

  const fetchMessages = async (threadId) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_IMAGE_BASE_URL}/thread/${threadId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSelectedThreadId(threadId);
      const msgs = res.data.thread.messages.map((msg) => ({
        id: msg._id,
        text: msg.content,
        sender: msg.role === 'user' ? 'user' : 'ai',
        rawText: msg.rawContent || msg.content,
      }));
      setMessages(msgs);
      setSidebarOpen(false);

      const foundExperienceId = msgs.find(
        (m) => m.sender === 'ai' && extractExperienceId(m.rawText || m.text)
      );
      if (foundExperienceId) {
        const expId = extractExperienceId(
          foundExperienceId.rawText || foundExperienceId.text
        );
        setExperienceId(expId);
        // Call fetchAssetsForExperience to properly handle sketchfabStatus
        fetchAssetsForExperience(expId);
      } else {
        setExperienceId(null);
        setAssets({
          mesh: [],
          image: [],
          video: [],
          audio: [],
        });
        setSelectedAsset(null);
        setProjectName('Untitled');
        setOriginalProjectName('');
        setSketchfabStatus(null);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteThread = async (threadId, e) => {
    e.stopPropagation();
    setThreadToDelete(threadId);
    setShowDeleteConfirm(true);
    setSidebarOpen(false);
  };

  const confirmDeleteThread = async () => {
    if (!threadToDelete) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_IMAGE_BASE_URL}/chat-thread/${threadToDelete}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setChatThreads(
        chatThreads.filter((thread) => thread._id !== threadToDelete)
      );
      if (selectedThreadId === threadToDelete) {
        setSelectedThreadId(null);
        setMessages([]);
        setExperienceId(null);
        setAssets({
          mesh: [],
          image: [],
          video: [],
          audio: [],
        });
        setSelectedAsset(null);
        setProjectName('Untitled');
        setOriginalProjectName('');
        setSketchfabStatus(null);
      }
      toast.success('The selected chat has been removed', {
        style: {
          background: '#131418',
          color: '#fff',
          borderRadius: '10px',
        },
        icon: '✅',
      });
    } catch (err) {
      console.error('Error deleting thread:', err);
      toast.error('Failed to delete chat thread');
    } finally {
      setShowDeleteConfirm(false);
      setThreadToDelete(null);
    }
  };

  const cancelDeleteThread = () => {
    setShowDeleteConfirm(false);
    setThreadToDelete(null);
  };

  const fetchAssetsForExperience = async (expId) => {
    if (!expId) {
      setProjectName('Untitled');
      setOriginalProjectName('');
      setAssets({
        mesh: [],
        image: [],
        video: [],
        audio: [],
      });
      setSketchfabStatus(null);
      return;
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/assets/project/${expId}/assets`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setProjectName(response.data?.data?.project_name || 'Untitled');
      setOriginalProjectName(response.data?.data?.project_name || '');

      const status = response.data?.data?.sketchfab_status;
      setSketchfabStatus(status);

      // Poll if status is not completed, disabled, or not_started
      if (!['completed', 'disabled', 'not_started'].includes(status)) {
        setTimeout(() => fetchAssetsForExperience(expId), 10000);
      }

      if (response.data.success && response.data.data.mesh) {
        setAssets((prevAssets) => ({
          ...prevAssets,
          mesh: response.data.data.mesh,
        }));
        setSelectedAsset(null);
      } else {
        console.warn('No assets found for experienceId:', expId);
        toast.error('No assets found for this experience');
      }
    } catch (error) {
      console.error('Error loading project data:', error);
      toast.error('Failed to load assets');
      setProjectName('Untitled');
      setSketchfabStatus(null);
    }
  };

  const fetchAssets = async () => {
    if (!experienceId) {
      setAssets({
        mesh: [],
        image: [],
        video: [],
        audio: [],
      });
      setProjectName('Untitled');
      setOriginalProjectName('');
      setSketchfabStatus(null);
      return;
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/assets/project/${experienceId}/assets`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setProjectName(response.data?.data?.project_name || 'Untitled');
      setOriginalProjectName(response.data?.data?.project_name || '');

      // Handle sketchfabStatus
      const status = response.data?.data?.sketchfab_status;
      setSketchfabStatus(status);

      if (response.data.success && response.data.data.mesh) {
        setAssets({
          mesh: response.data.data.mesh,
          image: [],
          video: [],
          audio: [],
        });
        setSelectedAsset(null);
      } else {
        console.warn('No assets found for experienceId:', experienceId);
        toast.error('No assets found for this experience');
      }
    } catch (error) {
      console.error('Error loading project data:', error);
      // toast.error('Failed to load assets');
      setProjectName('Untitled');
      setSketchfabStatus(null);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [experienceId, token]);

  const handleOpenInGrid = async () => {
    try {
      if (!experienceId) return;
      const experience = await axios.get(
        `${BASE_URL}/assets/project/${experienceId}/assets`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (experience.data.success) {
        navigate(`/gridpage/${experienceId}`);
      } else {
        toast.error(experience.data.message);
      }
    } catch (error) {
      console.error('Error opening in grid:', error);
      toast.error('Project not found');
    }
  };

  const handleMessageClick = (message) => {
    if (message.sender !== 'user') {
      const extractedId = extractExperienceId(message.rawText || message.text);
      if (extractedId) {
        setExperienceId(extractedId);
        fetchAssetsForExperience(extractedId);
      }
    }
  };

  const handleEditClick = () => {
    setIsEditingProjectName(true);
    setTimeout(() => {
      if (projectNameInputRef.current) {
        projectNameInputRef.current.focus();
        projectNameInputRef.current.select();
      }
    }, 100);
  };

  const handleProjectRename = async () => {
    if (!experienceId || !projectName.trim()) {
      console.error('Experience ID or project name is missing');
      setProjectName(originalProjectName || 'Untitled');
      setIsEditingProjectName(false);
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/experience/update/experience-name/${experienceId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ project_name: projectName.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setOriginalProjectName(projectName.trim());
      setIsEditingProjectName(false);
    } catch (error) {
      console.error('Project rename failed:', error);
      setProjectName(originalProjectName || 'Untitled');
      setIsEditingProjectName(false);
    }
  };

  const handleCancelEdit = () => {
    setProjectName(originalProjectName || 'Untitled');
    setIsEditingProjectName(false);
  };

  const handleProjectNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleProjectRename();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleClearInput = () => {
    setQuery('');
  };

  const handleSendQueryWithLoading = async () => {
    if (!query.trim() || aiLoading) return;

    const newMessage = { id: Date.now(), text: query, sender: 'user' };
    setMessages((prevMessages) => [...prevMessages, newMessage]);

    const currentQuery = query;
    setQuery('');
    setAiLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_IMAGE_BASE_URL}/query`,
        {
          query: currentQuery,
          ...(selectedThreadId && { threadId: selectedThreadId }),
          ...(experienceId && { experienceId }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSelectedThreadId(res.data.threadId);

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: Date.now(),
          text: res.data.cleanedResponse || res.data.response,
          sender: 'ai',
          rawText: res.data.response,
        },
      ]);
    } catch (error) {
      console.error(error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { id: Date.now(), text: `❌ Error: ${error.message}`, sender: 'ai' },
      ]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleKeyDownWithLoading = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQueryWithLoading();
    }
  };

  return (
    <>
      <div
        className={`flex gap-5 h-[calc(100vh-220px)] ${experienceId ? '' : 'w-full'}`}
      >
        <div
          className={`flex flex-col gap-8 ${experienceId ? 'min-w-[40vw] max-w-[50%]' : 'w-full'} h-[100%] relative`}
        >
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto overflow-x-hidden mcp-scrollbar relative"
          >
            <div className="w-[100%] flex flex-col relative">
              <div
                ref={historyRef}
                className="fixed lg:left-52 left-4 top-1/2 transform -translate-y-1/2 z-20"
              >
                <div className="relative bottom-6 flex items-center">
                  <div
                    className={`bg-[#1E1F22] border border-[#323131] w-[200px] transition-all duration-300 ${
                      sidebarOpen
                        ? 'opacity-100 h-[calc(100vh-210px)] overflow-y-auto scrollbar'
                        : 'opacity-0 w-0 max-h-0 overflow-hidden'
                    }`}
                  >
                    <div className="p-3 flex flex-col gap-5">
                      <div className="flex items-center gap-3">
                        <img src={iconsPath?.newChat} alt="" />
                        <button
                          onClick={() => {
                            setExperienceId(null);
                            setMessages([]);
                            setSelectedThreadId(null);
                            setSidebarOpen(false);
                            setSketchfabStatus(null);
                          }}
                          className="text-[13px] font-bold text-[#ffffff]"
                        >
                          New Chat
                        </button>
                      </div>
                      <h5 className="font-normal text-[12px]">Chats</h5>
                    </div>
                    <div className="overflow-y-auto scrollbar p-2 space-y-1">
                      {chatThreads.map((thread) => (
                        <div
                          key={thread._id}
                          onClick={() => fetchMessages(thread._id)}
                          className={`p-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-700/50 group ${
                            selectedThreadId === thread._id
                              ? 'bg-gray-700/30'
                              : 'hover:bg-gray-700/30'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p
                                className={`font-medium truncate text-xs ${
                                  selectedThreadId === thread._id
                                    ? 'text-[#979797]'
                                    : 'text-[#979797]'
                                }`}
                              >
                                {thread.title}
                              </p>
                            </div>
                            <button
                              onClick={(e) => handleDeleteThread(thread._id, e)}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded transition-all duration-200"
                            >
                              <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {chatThreads.length === 0 && (
                        <div className="p-3 text-center text-gray-500 text-xs">
                          No chat threads yet
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className={`absolute top-1/2 transform -translate-y-1/2 transition-all duration-300 hover:bg-[#28292B] rounded-2xl hover:scale-105 bg-[#1E1F22] px-1 py-4 border-none ${
                      sidebarOpen ? 'left-[208px]' : '-left-3'
                    }`}
                  >
                    {sidebarOpen ? (
                      <LucideArrowLeftToLine className="w-5 h-4 text-gray-400" />
                    ) : (
                      <LucideArrowRightToLine className="w-5 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-6 text-white mr-4">
                {messages.map((message, index) => (
                  <div
                    key={message.id}
                    className={`flex gap-4 animate-fade-in ${message.sender === 'user' ? 'flex-row-reverse' : ''}`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div
                      className={`max-w-[75%] ${message.sender === 'user' ? 'items-end' : 'items-start'} flex flex-col`}
                    >
                      <div
                        className={`rounded-2xl py-4 backdrop-blur-sm transition-all duration-200 ${
                          message.sender === 'user'
                            ? 'bg-[#1E1F22] px-6 text-white rounded-[20px]'
                            : 'text-gray-100 rounded-[20px]'
                        }`}
                      >
                        <p
                          className="text-sm text-[#979797]  leading-relaxed cursor-pointer"
                          onClick={() => handleMessageClick(message)}
                        >
                          {message.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {aiLoading && (
                  <div className="flex items-start p-8">
                    <div className="relative">
                      <div className="w-8 h-8 bg-[#EE2B2A] rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                          SG
                        </span>
                      </div>
                      <div className="absolute inset-0 rounded-full border-2 border-[#EE2B2A] animate-ping opacity-75"></div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          {showScrollButton && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-[#1E1F22] hover:bg-[#3a3a3a] text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105 z-10 border border-[#6A6A6A]"
              style={{ boxShadow: '0px 4px 12px rgba(0,0,0,0.3)' }}
            >
              <img className="h-4 w-4" src={iconsPath?.mcpScrollView} alt="" />
            </button>
          )}

          <div className="bg-[#1E1F22] w-full p-4 rounded-[30px] flex flex-col gap-4 z-10">
            <div className="w-full relative">
              <input
                type="text"
                value={query}
                onKeyDown={handleKeyDownWithLoading}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search, add, generate or ask anything"
                className="bg-transparent w-full text-sm text-white pr-10 focus:outline-none placeholder:text-gray-400"
                disabled={aiLoading}
              />
              {query.trim() && (
                <button
                  onClick={handleClearInput}
                  className="absolute top-1/2 right-2 transform -translate-y-1/2 p-1 hover:bg-[#3a3a3a] rounded-full transition-all duration-200"
                  style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                >
                  <X className="w-4 h-4 text-gray-400 hover:text-white" />
                </button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
              {/* <div className="flex items-center gap-2">
                <button
                  style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                  className="bg-[#28292B] p-2 rounded-md text-white hover:bg-[#3a3a3a]"
                  disabled={aiLoading}
                >
                  <img src={imagePath?.group96Image} alt="" />
                </button>
                <button
                  style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                  className="bg-[#28292B] flex items-center gap-2 text-white text-sm px-3 py-1.5 rounded-md hover:bg-[#3a3a3a]"
                  disabled={aiLoading}
                >
                  <img className="h-4 w-4" src={iconsPath?.mcpTool} alt="" />
                  Tools
                </button>
              </div> */}
              <div className="flex gap-4 ml-auto justify-end">
                <button
                  style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                  onClick={toggleListening}
                  disabled={aiLoading}
                  className={`text-white p-2 rounded-full ${
                    isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-[#28292B]'
                  } ${aiLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <img className="h-4 w-4" src={iconsPath?.mcpSpeaker} alt="" />
                </button>
                {query.trim() && (
                  <button
                    style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                    onClick={handleSendQueryWithLoading}
                    disabled={aiLoading}
                    className={`p-2 rounded-full transition-all duration-200 ${
                      aiLoading
                        ? 'bg-gray-600 cursor-not-allowed opacity-50'
                        : 'bg-[#28292B] hover:bg-red-600'
                    }`}
                  >
                    {aiLoading ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <img
                        className="h-4 w-4"
                        src={iconsPath?.mcpArrow}
                        alt=""
                      />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {experienceId && (
          <div className="relative w-full h-[100%]">
            <div className="relative w-full h-full overflow-hidden rounded-[20px]">
              {['completed', 'disabled', 'not_started'].includes(
                sketchfabStatus
              ) ? (
                <ScenePreview
                  experienceId={experienceId}
                  assets={selectedAsset ? [selectedAsset] : assets?.mesh}
                  skipLoading={true}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <span className="text-gray-400 text-lg">
                    Loading assets...
                  </span>
                </div>
              )}
            </div>
            <div className="absolute bottom-6 left-6 flex flex-col sm:flex-row items-center gap-2 z-10">
              <button
                onClick={handleOpenInGrid}
                className={`px-5 text-[12px] py-2 rounded-lg transition-all duration-200 flex items-center justify-center shadow-lg ${
                  experienceId
                    ? 'bg-[#EE2B2A] text-white hover:bg-[#CC2524]'
                    : 'bg-gray-600 opacity-50 cursor-not-allowed'
                }`}
                disabled={!experienceId}
              >
                Open in Grid
              </button>
              <div className="rounded-lg flex items-center gap-1 relative">
                <div className="relative">
                  <input
                    ref={projectNameInputRef}
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    onKeyDown={handleProjectNameKeyDown}
                    placeholder="Enter project name..."
                    disabled={!isEditingProjectName || !experienceId}
                    className={`bg-[#28292B] border text-[12px] px-1 py-1 pr-8 focus:outline-none placeholder:text-gray-400 rounded-[5px] w-[130px] transition-all duration-200 ${
                      experienceId
                        ? 'text-white border-[#8C8C8C]'
                        : 'text-gray-400 border-gray-600 opacity-50 cursor-not-allowed'
                    } ${!isEditingProjectName && experienceId ? 'cursor-pointer' : ''}`}
                    onClick={
                      !isEditingProjectName && experienceId
                        ? handleEditClick
                        : undefined
                    }
                  />
                  {projectName && experienceId && (
                    <button
                      onClick={
                        isEditingProjectName
                          ? handleProjectRename
                          : handleEditClick
                      }
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 p-1 rounded transition-all duration-200"
                      style={{ boxShadow: '0px 0px 4px rgba(0,0,0,0.3)' }}
                    >
                      {isEditingProjectName ? (
                        <MoveRight className="w-3 h-6 text-white" />
                      ) : (
                        <Edit3 className="w-3 h-3 text-gray-400" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {showChatInterface && (
        <div className="py-4 px-6 rounded-[20px] bg-[#1E1F22] backdrop-blur min-h-[70px]">
          {experienceId && <h1 className="mb-2 text-[12px]">Scene Content</h1>}
          <div className="flex items-center gap-2  min-h-[30px] flex-wrap">
            {assets?.mesh && assets.mesh.length > 0 ? (
              assets.mesh.map((asset, index) => (
                <div
                  key={index}
                  onClick={() =>
                    setSelectedAsset(selectedAsset === asset ? null : asset)
                  }
                  className={`flex items-center gap-2 bg-[#121316] px-2 rounded-lg border border-[#3B3B3B] transition-all duration-200 group cursor-pointer flex-shrink-0 ${
                    selectedAsset === asset
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-700/50 hover:border-gray-600/50'
                  }`}
                >
                  <span
                    className={`text-[10px] whitespace-nowrap ${selectedAsset === asset ? 'text-blue-200' : 'text-gray-300'}`}
                  >
                    {asset.file_name}
                  </span>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await axios.delete(
                          `${BASE_URL}/assets/mesh/${asset._id}`,
                          {
                            headers: {
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                            },
                          }
                        );
                        setAssets((prev) => ({
                          ...prev,
                          mesh: prev.mesh.filter((a) => a._id !== asset._id),
                        }));
                        if (selectedAsset?._id === asset._id) {
                          setSelectedAsset(null);
                        }
                        toast.success('Asset deleted successfully', {
                          style: {
                            background: '#131418',
                            color: '#fff',
                            borderRadius: '10px',
                          },
                          icon: '✅',
                        });
                      } catch (error) {
                        console.error('Error deleting asset:', error);
                        toast.error('Failed to delete asset');
                      }
                    }}
                    className="p-1 hover:bg-gray-700/50 rounded-full transition-all duration-200"
                  >
                    <X className="w-4 h-4 text-gray-400 group-hover:text-gray-300" />
                  </button>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center w-full h-[40px]">
                <span className="text-gray-500 text-sm">
                  {experienceId ? 'No assets available' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={historyRef}
            className="bg-[#131418] rounded-2xl py-14 w-[90%] max-w-lg text-center shadow-lg"
          >
            <div className="flex justify-center mb-4">
              <div className="w-[75px] h-[75px] rounded-full border border-[#EE2B2A] bg-[#EE2B2A] bg-opacity-20 flex items-center justify-center">
                <img src={iconsPath?.historyDeleteWarning} alt="" />
              </div>
            </div>
            <h2 className="text-white text-[26px] font-bold mb-2">
              Confirmation Delete?
            </h2>
            <p className="text-[#fff] text-opacity-60 text-[20px] mb-6">
              Are you sure you want to delete this chat?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={cancelDeleteThread}
                className="bg-[#31333A] text-white text-sm font-medium px-16 py-2 rounded-md hover:bg-[#444] transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteThread}
                className="bg-[#EE2B2A] text-white text-sm font-medium px-16 py-2 rounded-md hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}

export default memo(ChatInterface);
